'use client';

import { c as _c } from "react/compiler-runtime";
import cx from 'classix';
import { formatDistance } from 'date-fns';
// import { type EmblaCarouselType } from 'embla-carousel';
import useEmblaCarousel from 'embla-carousel-react';
// import Image from 'next/image';
import { useLocale, useTranslations } from 'next-intl';
import { type MouseEvent, useState } from 'react';
import { FaHeart } from 'react-icons/fa6';
import { NextButton, PrevButton, useCarouselArrows } from '@/features/listing/components/listing-card/listing-carousel-arrows/use-carousel-arrows';
import ListingCarouselSlide from '@/features/listing/components/listing-card/listing-carousel-slide/listing-carousel-slide';
import createListingBookmark from '@/features/listing/requests/create-listing-bookmark';
import deleteListingBookmark from '@/features/listing/requests/delete-listing-bookmark';
import { type Listing } from '@/features/listing/types/listing-types';
import useAppFlyerLink from '@/features/roomster-app/hooks/use-app-flyer-link';
import { getUserBearerCookie } from '@/features/user/cookies/cookie-user-bearer';
import { useRouter } from '@/i18n/routing';
import getDateFnsLocale from '@/libs/date-fns/get-date-fns-locale';
import { ROUTER_LINKS } from '@/shared/constants/links-constants';
import { type ImageThumbnail } from '@/shared/types/image-thumbnail-types';
import styles from './listing-carousel.module.scss';
interface ListingCarouselProps {
  images: ImageThumbnail[];
  imagesAlt: string;
  isNew: Listing['isNew'];
  isBookmarked: Listing['isBookmarked'];
  listingRefreshedDate: Listing['refreshed'];
  listingId: Listing['id'];
  listingTypeId: Listing['listingTypeId'];
  isHighPriority?: boolean;
}
export default function ListingCarousel(props) {
  const $ = _c(46);
  const {
    images,
    imagesAlt,
    isBookmarked,
    isHighPriority,
    isNew,
    listingId,
    listingRefreshedDate,
    listingTypeId
  } = props;
  const listingLabelsTranslations = useTranslations("listing.labels");
  const locale = useLocale();
  const router = useRouter();
  const appFlyerMobileBookmarkLink = useAppFlyerLink("mobile", "mobileListingBookmark", ROUTER_LINKS.phoneAuth);
  const [isListingBookmarked, setIsListingBookmarked] = useState(isBookmarked);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      align: "start",
      loop: true
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [emblaRef, emblaApi] = useEmblaCarousel(t0);
  let t1;
  if ($[1] !== listingRefreshedDate || $[2] !== locale) {
    t1 = formatDistance(listingRefreshedDate, new Date(), {
      addSuffix: true,
      locale: getDateFnsLocale(locale)
    });
    $[1] = listingRefreshedDate;
    $[2] = locale;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const relativeRefreshDate = t1;
  let t2;
  if ($[4] !== appFlyerMobileBookmarkLink || $[5] !== isListingBookmarked || $[6] !== listingId || $[7] !== listingTypeId || $[8] !== router) {
    t2 = event => {
      event.stopPropagation();
      event.preventDefault();
      const loggedUser = getUserBearerCookie();
      if (loggedUser) {
        if (isListingBookmarked) {
          setIsListingBookmarked(false);
          deleteListingBookmark(listingId, listingTypeId).catch(_temp);
        } else {
          setIsListingBookmarked(true);
          createListingBookmark(listingId, listingTypeId).catch(_temp2);
        }
      } else {
        router.push(appFlyerMobileBookmarkLink);
      }
    };
    $[4] = appFlyerMobileBookmarkLink;
    $[5] = isListingBookmarked;
    $[6] = listingId;
    $[7] = listingTypeId;
    $[8] = router;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const handleBookmarkClick = t2;
  const {
    nextBtnDisabled,
    onNextButtonClick,
    onPrevButtonClick,
    prevBtnDisabled
  } = useCarouselArrows(emblaApi);
  const t3 = isListingBookmarked && styles.embla_bookmarkIcon__isBookmarked;
  let t4;
  if ($[10] !== t3) {
    t4 = cx(styles.embla_bookmarkIcon, t3);
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== t4) {
    t5 = <FaHeart className={t4} />;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== handleBookmarkClick || $[15] !== t5) {
    t6 = <button className={styles.embla_bookmark} aria-label="favorite" onClick={handleBookmarkClick}>{t5}</button>;
    $[14] = handleBookmarkClick;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== isNew || $[18] !== listingLabelsTranslations) {
    t7 = isNew && <div className={styles.embla_isNew}>{listingLabelsTranslations("new")}</div>;
    $[17] = isNew;
    $[18] = listingLabelsTranslations;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== relativeRefreshDate) {
    t8 = <div className={styles.embla_relativeDate}>{relativeRefreshDate}</div>;
    $[20] = relativeRefreshDate;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== images || $[23] !== imagesAlt || $[24] !== isHighPriority) {
    let t10;
    if ($[26] !== imagesAlt || $[27] !== isHighPriority) {
      t10 = (image, index) => <ListingCarouselSlide index={index} key={image.id} src={image.path} alt={imagesAlt} isHighPriority={isHighPriority} />;
      $[26] = imagesAlt;
      $[27] = isHighPriority;
      $[28] = t10;
    } else {
      t10 = $[28];
    }
    t9 = images.map(t10);
    $[22] = images;
    $[23] = imagesAlt;
    $[24] = isHighPriority;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[29] !== t9) {
    t10 = <div className={styles.embla__container}>{t9}</div>;
    $[29] = t9;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  let t11;
  if ($[31] !== emblaRef || $[32] !== t10) {
    t11 = <div className={styles.embla__viewport} ref={emblaRef}>{t10}</div>;
    $[31] = emblaRef;
    $[32] = t10;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  let t12;
  if ($[34] !== images.length || $[35] !== nextBtnDisabled || $[36] !== onNextButtonClick || $[37] !== onPrevButtonClick || $[38] !== prevBtnDisabled) {
    t12 = images.length > 1 && <div className={styles.embla__buttons}><PrevButton onClick={onPrevButtonClick} disabled={prevBtnDisabled} /><NextButton onClick={onNextButtonClick} disabled={nextBtnDisabled} /></div>;
    $[34] = images.length;
    $[35] = nextBtnDisabled;
    $[36] = onNextButtonClick;
    $[37] = onPrevButtonClick;
    $[38] = prevBtnDisabled;
    $[39] = t12;
  } else {
    t12 = $[39];
  }
  let t13;
  if ($[40] !== t11 || $[41] !== t12 || $[42] !== t6 || $[43] !== t7 || $[44] !== t8) {
    t13 = <section className={styles.embla}>{t6}{t7}{t8}{t11}{t12}</section>;
    $[40] = t11;
    $[41] = t12;
    $[42] = t6;
    $[43] = t7;
    $[44] = t8;
    $[45] = t13;
  } else {
    t13 = $[45];
  }
  return t13;
}
function _temp2() {
  return null;
}
function _temp() {
  return null;
}